/**
 * Help articles for the in-app HelpDrawer.
 *
 * Each article is a flat list of blocks. Block types:
 *
 *   { type: "h2",   text }
 *   { type: "p",    text }                       // body paragraph
 *   { type: "ul",   items: string[] }            // bullet list
 *   { type: "ol",   items: string[] }            // numbered list (rendered like ul, with index)
 *   { type: "kbd",  combo, label }               // keyboard hint row
 *   { type: "img",  src, caption? }              // screenshot (under /help/*.jpg)
 *   { type: "note", text }                       // amber callout
 *   { type: "code", text }                       // mono code/example block
 *
 * Voice: plain English, friendly, occasional dry humor. Every piece of jargon
 * gets defined the first time it appears. No marketing fluff.
 *
 * Screenshots live in /app/frontend/public/help/ and are referenced as
 * /help/<filename>.jpg by the browser.
 */

export const ARTICLES = [
  // ─── 1. Welcome ───────────────────────────────────────────────────────────
  {
    id: "welcome",
    title: "Welcome",
    summary: "What this is, who it's for, and how it differs from a Gantt chart.",
    blocks: [
      { type: "p", text: "Hi. This is a production schedule tool built for music festivals and live events. It looks like a spreadsheet on purpose — you can see the whole day at once, you can edit any cell without opening a popup, and dragging things around actually does something useful." },
      { type: "p", text: "If you've ever tried to run show day off a Gantt chart, you already know why this exists. Gantt charts are pretty. They are also useless when the truss build runs 45 minutes long and you need to figure out, right now, what to push." },
      { type: "h2", text: "Who this is for" },
      { type: "ul", items: [
        "Production managers running multi-day, multi-stage builds.",
        "Stage / department leads who need one source of truth for crew, equipment, and timing.",
        "Producers who want to spot drift in real time and replan without opening a spreadsheet.",
      ]},
      { type: "h2", text: "What you'll learn in these docs" },
      { type: "p", text: "We'll walk through building a 3-day festival from a blank slate — same path most of our users take on day one. By the end you'll have set up a project, added stages and crew, scheduled a build day, learned how to handle drift, allocated gear, and printed something you can hand to a runner." },
      { type: "img", src: "/help/01-grid.jpg", caption: "The schedule grid. Rows are tasks. Columns on the left are time/room/title/status; columns on the right are one per crew member." },
      { type: "note", text: "If you'd rather poke around first, there's a built-in demo project called 'Riverside Festival 2026' loaded with stages, crew, and a few days of work. Everything in these docs uses it as the example." },
    ],
  },

  // ─── 2. Spin up your festival ─────────────────────────────────────────────
  {
    id: "setup-project",
    title: "Step 1 · Spin Up Your Festival",
    summary: "Create your project, set the dates, pick the venue timezone.",
    blocks: [
      { type: "p", text: "A 'project' is one festival, one tour stop, one corporate gig — whatever discrete event you're planning. Everything else (rooms, crew, equipment, tasks) lives inside a project. You can have as many as you like." },
      { type: "h2", text: "Open the Projects page" },
      { type: "p", text: "Click your current project name in the top-left of the schedule. The Projects page shows everything you have access to." },
      { type: "img", src: "/help/03-projects-page.jpg", caption: "The Projects page. Each card is a project — click to open, or hit NEW PROJECT to start fresh." },
      { type: "h2", text: "Create a new project" },
      { type: "p", text: "Click NEW PROJECT. Fill in three things:" },
      { type: "ol", items: [
        "PROJECT NAME — what you'll see in the dropdown later. Be specific: 'Riverside 2026' beats 'Festival'.",
        "DATE RANGE — start and end days of your build + show. Every day inside the range will appear in the grid, even days you haven't planned yet. That's intentional — empty days are easier to plan into than days you have to conjure.",
        "VENUE TIMEZONE — this is what gets printed on every run sheet. If your crew flies in from three countries, set this to where the venue is. Everyone's watch is going to be on venue time anyway.",
      ]},
      { type: "img", src: "/help/04-new-project-dialog.jpg", caption: "The New Project dialog. Name, dates, timezone — that's it." },
      { type: "p", text: "Click CREATE. You'll land in an empty schedule grid, ready to add rooms and people." },
      { type: "note", text: "You can change the project name and date range later from the project settings (cog icon, top right of the schedule). Adding days at the end is safe; shrinking the range from days that already have tasks will warn you first." },
    ],
  },

  // ─── 3. Rooms + Crew ─────────────────────────────────────────────────────
  {
    id: "setup-rooms-crew",
    title: "Step 2 · Add Stages & Crew",
    summary: "Define your rooms (stages, FOH, backstage) and the people working them.",
    blocks: [
      { type: "p", text: "Two pieces of structure to add before tasks: rooms and crew. Both have a dedicated panel in the top bar." },

      { type: "h2", text: "Rooms (a.k.a. stages, FOH, backstage…)" },
      { type: "p", text: "A 'room' is anywhere work happens. For most festivals that's:" },
      { type: "ul", items: [
        "One per stage (Stage A, Stage B, Stage C)",
        "FOH (front of house)",
        "Backstage / production office",
        "A 'strike zone' for breakdown",
      ]},
      { type: "p", text: "Click the building icon in the top bar to open the Rooms panel. For each room, pick a name, a short abbreviation (3–4 letters — this is what shows up everywhere on the grid), and a color. Colors aren't decoration — they're how you'll scan the schedule at 6am during a long shift." },
      { type: "img", src: "/help/05-rooms-panel.jpg", caption: "Rooms panel (right side). Add, rename, recolor, reorder. The abbreviation is what shows in the ROOM column on the grid." },
      { type: "note", text: "Reorder rooms by dragging. The order here is the order they appear in the filter chips at the top of the grid, and the order on every printed report. Put your busy stages first." },

      { type: "h2", text: "Crew" },
      { type: "p", text: "Click the person icon in the top bar to open the Crew panel. Add each crew member with their name and primary role (LX programmer, Stage Manager, A1, A2, Rigger…). Roles are free text — write what your team actually calls them." },
      { type: "img", src: "/help/06-crew-panel.jpg", caption: "Crew panel. Each person gets a name and a role; both show up at the top of their column in the grid." },
      { type: "p", text: "Once a crew member exists, they get their own column on the right side of the schedule grid. That column shows what they're doing, when they're free, and when they're walking between stages." },
      { type: "note", text: "Bring your crew over in waves. You don't need to add everyone on day one — start with department heads, add the rest of the rigging team the week of the build, and so on. The grid stays readable up to about 30 columns." },
    ],
  },

  // ─── 4. First tasks ─────────────────────────────────────────────────────
  {
    id: "first-tasks",
    title: "Step 3 · Schedule Your First Day",
    summary: "Two ways to add a task: the fast way and the proper way.",
    blocks: [
      { type: "p", text: "Now the fun part. You've got an empty grid with day banners (Wed 20 May, Thu 21 May, etc.) and that's it. Time to put work on the calendar." },

      { type: "h2", text: "The fast way: quick-add" },
      { type: "p", text: "Under each day banner there's a row that says + NEW TASK with a placeholder reading QUICK-ADD: TYPE TITLE + ENTER. Click in, type a title, press Enter. That's it. A 1-hour task gets created at the next sensible time slot (usually right after the last task on that day, or at 09:00 if the day is empty)." },
      { type: "img", src: "/help/07-quick-add.jpg", caption: "The quick-add row under each day. Type, Enter, move on." },
      { type: "p", text: "Use this when you're brainstorming — get titles down first, fix the times later. You can drag rows around, edit times in place, change rooms with a click. Don't overthink it." },

      { type: "h2", text: "The proper way: full new task dialog" },
      { type: "p", text: "Click the + NEW TASK button itself (not the input next to it). The full dialog opens. Use this when you want to set:" },
      { type: "ul", items: [
        "Specific duration (not just 1 hour)",
        "Subtasks (e.g. 'PA hang — Stage A' with children 'Sub array', 'Mains', 'Delays')",
        "Recurrence (daily safety briefings, etc.)",
        "Priority (Critical / High / Normal / Low)",
        "Room, notes, deadlines",
      ]},
      { type: "img", src: "/help/08-new-task-dialog.jpg", caption: "Full new-task dialog. The AI ASSIST strip can estimate duration and propose subtasks — click APPLY to fill them in." },
      { type: "h2", text: "Floating tasks" },
      { type: "p", text: "A 'floating' task has no scheduled time yet — it's something that needs to happen today but you don't know exactly when. Things like 'walkie audit + recharge' or 'lunch delivery'. Create them with the FLT checkbox in the dialog; they show up in the FLOATING tray at the top of the day, ready to be dropped onto the timeline (or auto-placed by the AI copilot — more on that later)." },
      { type: "note", text: "If you only do one thing differently from a spreadsheet: lean into quick-add. Most people overshoot on detail in the first pass. A 1-hour placeholder you adjust later beats a 23-minute task you obsess over for 10 minutes." },
    ],
  },

  // ─── 5. Schedule grid ────────────────────────────────────────────────────
  {
    id: "schedule-grid",
    title: "Living in the Grid",
    summary: "Columns, drag-to-move, inline editing, multi-select, resizing.",
    blocks: [
      { type: "p", text: "The grid is where you'll spend most of your time. Worth knowing every gesture it understands." },

      { type: "h2", text: "Reading the columns" },
      { type: "ul", items: [
        "START / FINISH — local wall-clock times. Click to step ±15m / ±1h.",
        "DUR — duration. Read-only here; change it by editing FINISH or by dragging the bottom edge of the row.",
        "ROOM — color-coded room abbreviation. Click to reassign.",
        "TASK / SUBTASK — title with optional priority glyphs (! = high, ‼ = critical) and FLT / DDL badges.",
        "STATUS — pill that cycles PLANNED → IN PROG → DONE → BLOCKED → SPILLED when clicked.",
        "NOTES · BLOCKERS — notes pencil + blocker flag count.",
        "CREW COLUMNS — one per visible crew member. Shows their assigned role pill, transfers between rooms, and free time gaps.",
      ]},

      { type: "h2", text: "The top bar" },
      { type: "img", src: "/help/02-topbar.jpg", caption: "Top bar. Project switcher left, drift indicator and AI status middle, density and view controls, then NOW · WRAP · SCHEDULE · EXPORT · SHARE on the right." },
      { type: "ul", items: [
        "Building icon → Rooms panel.   Person icon → Crew panel.   Box icon → Equipment.",
        "DRIFT +0m — how much your in-progress tasks are ahead of (negative) or behind (positive) schedule, summed across the project.",
        "AI · X/Y — AI copilot status. Green dot = ready; amber = thinking.",
        "Density toggles (the three rectangles) — collapse rows for more on screen.",
        "NOW — jump to the red 'now' line in the grid.",
        "WRAP — generate a Daily Wrap (more in the AI Copilot article).",
      ]},

      { type: "h2", text: "Drag a row to move it" },
      { type: "p", text: "Hover the left edge of any row — a drag grip appears. Drag the row up or down and drop it onto another row. The dragged task snaps to start RIGHT AFTER the target's finish time. A floating badge shows the new start time and how much it moved." },
      { type: "p", text: "This is the bread-and-butter gesture. 'PA hang ran over → drag the LX rig onto it, everything after shuffles.' One drag, done." },

      { type: "h2", text: "Inline editing" },
      { type: "p", text: "Almost every cell edits in place:" },
      { type: "ul", items: [
        "Click on title text → input replaces the cell. Type. Enter to save, Esc to cancel.",
        "Click the START or FINISH time → ±15m / ±1h stepper.",
        "Click the STATUS pill → cycles to the next status.",
        "Click the ROOM cell → room picker dropdown.",
        "Click the NOTES pencil → inline note editor.",
      ]},

      { type: "h2", text: "Resize duration" },
      { type: "p", text: "Hover the bottom edge of any row to expose a resize handle. Drag down to extend, up to shorten. Snaps to 15-minute increments. Useful for stretching a task to fill until the next anchor." },

      { type: "h2", text: "Multi-select" },
      { type: "p", text: "Click a row to select it. Shift-click another row to range-select everything between. Cmd / Ctrl-click to toggle individual rows. A bulk action bar appears at the bottom: shift all selected tasks by ±15m, change status, lock, or delete in one go." },

      { type: "h2", text: "Filters" },
      { type: "p", text: "Top bar has chips for room filter (STA, STB, STC, FOH, BCK, SZ…) and status filter (PLANNED, IN PROG, DONE, BLOCKED, SPILLED). And priority (LOW, NORMAL, HIGH, CRITICAL). Click to toggle. They combine — 'show me everything CRITICAL on Stage A that's still PLANNED' is two clicks." },
      { type: "note", text: "Filters are remembered across sessions in your browser. If you open the app and it looks empty, check whether you've filtered yourself into a corner." },
    ],
  },

  // ─── 6. Status, drift, blockers ───────────────────────────────────────────
  {
    id: "status-and-drift",
    title: "Status, Drift & Blockers",
    summary: "What the colors mean, what 'drift' is, and how blockers show up.",
    blocks: [
      { type: "h2", text: "Status colors" },
      { type: "ul", items: [
        "PLANNED — no color rail. Default for everything you haven't started.",
        "IN PROG — amber left rail. Work is happening now.",
        "DONE — row dims to 50% opacity. You can still click it; it just gets out of the way.",
        "BLOCKED — red left rail. Something stopped this task. Often auto-applied when a critical blocker is attached.",
        "SPILLED — cyan left rail. A subtask that didn't finish today and got rolled forward to tomorrow. Tracks provenance.",
      ]},
      { type: "p", text: "Cycling status is one click on the pill. There's no save button — every click syncs to the server." },

      { type: "h2", text: "What is drift?" },
      { type: "p", text: "Drift = the live difference between when a task was supposed to end and when it's actually going to end based on what's IN PROG. Positive drift = running late. Negative drift = running ahead (rare, congratulations)." },
      { type: "p", text: "You'll see drift in three places, escalating in urgency:" },
      { type: "ol", items: [
        "Inline +Xm chip on the finish cell — small drifts (under 15 minutes). No big deal.",
        "Single toast strip — medium drifts (16–45 minutes). Shows up once per source task per 30 minutes so it doesn't spam you.",
        "Persistent banner above the grid — large drifts (over 45 minutes) or any drift on a LOCKED task. These don't disappear until you dismiss them or the drift goes away.",
      ]},

      { type: "h2", text: "Locks" },
      { type: "p", text: "A locked task (padlock icon at the start cell) cannot be dragged, resized, or time-edited. Use it for fixed anchors: doors open, headliner downbeat, curfew. If something upstream slips, the locked task can't move — so the system surfaces a red 'LOCKED · KNOCK-ON' banner showing the chain of damage." },
      { type: "p", text: "Lock things sparingly. Lock everything and you've just made yourself a Gantt chart again." },

      { type: "h2", text: "Blockers" },
      { type: "p", text: "Blockers are reasons a task can't proceed. Each has:" },
      { type: "ul", items: [
        "A severity: INFO (FYI), WARNING (eyes on it), CRITICAL (we're stuck).",
        "A description ('Waiting on local fire marshal sign-off').",
        "A link back to a task.",
      ]},
      { type: "p", text: "Critical blockers auto-flip the task to BLOCKED status. Resolve a blocker (mark it cleared in the task detail drawer) and the task returns to whatever it was before." },
      { type: "p", text: "The NOTES · BLOCKERS column on the grid shows a flag count + the highest severity color. Click the row's title to open the full drawer and see the list." },

      { type: "note", text: "Dismissing a drift banner is temporary. If the drift grows another 15 minutes from the same source, the banner comes back. The system isn't trying to lecture you — it just doesn't want a 90-minute slip hidden behind a stale dismissal." },
    ],
  },

  // ─── 7. AI Copilot ───────────────────────────────────────────────────────
  {
    id: "ai-copilot",
    title: "AI Copilot",
    summary: "Cmd+K command palette, estimator, daily wrap, new day rollover.",
    blocks: [
      { type: "p", text: "The AI copilot is opt-in everywhere — nothing happens automatically without you clicking APPLY or CONFIRM. It runs on Claude Sonnet behind the scenes. Your project data stays in your project; payloads sent to the model are scoped to the resource you're asking about." },

      { type: "h2", text: "Cmd + K · The command palette" },
      { type: "kbd", combo: "Cmd K", label: "open the command palette from anywhere" },
      { type: "p", text: "Type what you want in plain English. The palette interprets it, returns a structured plan, and shows you exactly what it'll do before doing it." },
      { type: "img", src: "/help/09-cmdk-palette.jpg", caption: "Cmd+K palette. Type a sentence, get a confirmable action plan." },
      { type: "p", text: "Things that work:" },
      { type: "ul", items: [
        "'move Stage A truss build to tomorrow 09:00'",
        "'add a safety briefing every weekday at 07:30 for the next 4 days'",
        "'shift everything on Stage B by 30 minutes'",
        "'show me what crew Vidal is doing Thursday'",
        "'mark all FOH tasks as DONE'",
      ]},

      { type: "h2", text: "The estimator" },
      { type: "p", text: "Inside the new-task dialog (and the parent-task drawer), there's an AI ASSIST strip with two buttons: ESTIMATE and SUBTASKS. ESTIMATE gives you a three-point duration estimate (best case · expected · worst case) with a confidence score and a one-line rationale. Click APPLY to fill the duration field." },
      { type: "p", text: "This is most useful for tasks you've never timed before. 'How long does it take to dress 24 mics?' — the model has seen enough festival logs to make a reasonable guess. Don't trust it blindly. Do trust it as a sanity check against your own gut." },

      { type: "h2", text: "Subtask suggester" },
      { type: "p", text: "Click SUBTASKS next to ESTIMATE. The model proposes 3–6 likely children for your parent task with per-step durations and short rationales. INSERT individual ones, or INSERT ALL. Useful when you know the parent's name but don't want to type out every subtask by hand." },

      { type: "h2", text: "Daily Wrap" },
      { type: "p", text: "Click WRAP in the top bar at the end of the day. Pick a date, click GENERATE. The AI produces a narrative summary of what happened that day — drift, completed work, outstanding issues, what's queued for tomorrow." },
      { type: "img", src: "/help/10-daily-wrap.jpg", caption: "Daily Wrap. Narrative + highlights / concerns / tomorrow lists. Copy-paste it into your production Slack or email." },
      { type: "p", text: "The output is plain text. No formatting tricks, no AI weirdness — just the report you'd write yourself after every show day if you weren't exhausted." },

      { type: "h2", text: "New Day rollover" },
      { type: "p", text: "If tasks from yesterday didn't finish, the New Day dialog pops up when you open the app the next morning. For each unfinished task it offers four actions:" },
      { type: "ul", items: [
        "→ TODAY — roll it to today.",
        "DONE — mark it done after the fact.",
        "FLOAT — convert it to a floating task to be placed later.",
        "SKIP — leave it as-is (you'll deal with it manually).",
      ]},
      { type: "p", text: "Bulk-apply the same action to everything with the top buttons, or pick per task. Hit APPLY and your schedule's been triaged before your coffee finishes brewing." },
      { type: "note", text: "If the AI copilot ever proposes something you didn't expect, just close the dialog. Nothing's committed until you click the apply button." },
    ],
  },

  // ─── 8. Equipment ─────────────────────────────────────────────────────────
  {
    id: "equipment",
    title: "Equipment",
    summary: "Categories, pool stock, room allocations, shortfalls, status tracking.",
    blocks: [
      { type: "p", text: "Click the box icon in the top bar (or the EQUIPMENT link from the schedule view) to open the Equipment view. It's where you track what gear exists, where it's allocated, and whether it's actually going to show up." },

      { type: "h2", text: "The two-tab layout" },
      { type: "p", text: "Top of the Equipment view, you'll see two tabs:" },
      { type: "ul", items: [
        "ALLOCATIONS — left side lists your rooms; right side lists your inventory. Drag from right to left to allocate gear to a room.",
        "STATUS — flat list of every piece of equipment with its procurement state (PLANNED, ORDERED, SHIPPED, ARRIVED) and ETA.",
      ]},
      { type: "img", src: "/help/12-equipment-allocations.jpg", caption: "Equipment view, Allocations tab. Rooms on the left, inventory tiles on the right." },

      { type: "h2", text: "Pool · Used · Available · Shortfall" },
      { type: "p", text: "Each inventory tile shows three numbers, all of which sound similar but mean different things:" },
      { type: "ul", items: [
        "POOL — how many you physically have / will have. ('We've got 40 XLR cables.')",
        "USED — how many you've allocated across all rooms. ('38 are spoken for.')",
        "AVAIL — POOL minus USED. ('2 spare.')",
      ]},
      { type: "p", text: "If you allocate more than your pool, AVAIL goes negative and turns red — that's a SHORTFALL. The system lets you do this on purpose — it's the cleanest way to say 'we need to hire 4 more, here's the gap'. Click the SHORTFALL chip at the top to filter to just the problem items." },

      { type: "h2", text: "Allocating gear to a room" },
      { type: "p", text: "Drag an inventory tile from the right onto a room on the left. A small dialog asks how many — type a number, press Enter. The allocation appears under the room with its qty. Adjust later by clicking the allocation row." },
      { type: "p", text: "An item can be allocated to multiple rooms (e.g. 8 mics on Stage A, 4 on Stage B). The system keeps a running total and tells you when you've over-committed." },

      { type: "h2", text: "Categories (Sound, LX, Video, Comms, Power…)" },
      { type: "p", text: "Click CATEGORIES in the top right to manage your project's category list. Once defined, each equipment item gets dropped into a category — and the inventory side groups by category for fast scanning. The filter chip strip at the top of the rooms list also gives you per-category filtering." },
      { type: "note", text: "Categories are per-project, not global. Each show might have a different breakdown — that's fine. Start with broad strokes ('Sound', 'LX', 'Video', 'Power') and add finer ones if you actually need them." },

      { type: "h2", text: "Editing an equipment item" },
      { type: "p", text: "Click any inventory tile to open the edit dialog. From here you can change everything about that line item:" },
      { type: "img", src: "/help/13-equipment-edit.jpg", caption: "Equipment edit dialog. Label, pool qty, category, acquisition kind, cost, status, ETA, vendor, notes." },
      { type: "ul", items: [
        "Label and pool quantity.",
        "Category.",
        "Acquisition: HIRED (rental) or PURCHASED (you own it).",
        "Cost — single number; the procurement total for this line.",
        "Status: PLANNED → ORDERED → SHIPPED → ARRIVED.",
        "ETA: when it's expected on site (the date pickup).",
        "Vendor: hire company or supplier name.",
        "Notes: serial numbers, riders, gotchas.",
      ]},
      { type: "p", text: "Press SAVE (or Cmd+Enter). Press DELETE in the bottom-left to wipe the item and all its room allocations." },

      { type: "h2", text: "Tracking what's actually shipped" },
      { type: "p", text: "Switch to the STATUS tab. It's a flat table of every equipment line with its status, ETA, vendor, and cost — sortable, filterable by status." },
      { type: "img", src: "/help/14-equipment-status.jpg", caption: "Equipment Status tab. Every item, every status, every ETA — one screen." },
      { type: "p", text: "Pre-show, walk through the list and flip statuses as gear ships and arrives. Anything still in PLANNED two weeks out is a yellow flag. Anything still in ORDERED one week out is a phone call." },
    ],
  },

  // ─── 9. Printing & exports ────────────────────────────────────────────────
  {
    id: "printing-exports",
    title: "Printing & Exports",
    summary: "Run sheets, hit lists, pull lists — PDF and CSV.",
    blocks: [
      { type: "p", text: "The grid is great for editing. It's not what you hand to a runner at 8am with a clipboard. That's what reports are for." },
      { type: "h2", text: "Where to find them" },
      { type: "p", text: "Click EXPORT in the top bar. The menu lists every report available. PDFs open in a new tab (so you can print or save from there). CSVs download immediately." },
      { type: "img", src: "/help/11-export-menu.jpg", caption: "Export menu. PDFs open in a new tab; CSVs download." },

      { type: "h2", text: "Master Schedule" },
      { type: "p", text: "PDF or CSV. The full run sheet — every task in date/time order, grouped by day with banner rows. The PDF is landscape A4, designed to be readable in tent lighting. The CSV is the same data with extra UTC and ISO columns for downstream tools." },
      { type: "p", text: "Print it for: morning brief, full-team handoff, post-show archive." },

      { type: "h2", text: "Hit List" },
      { type: "p", text: "PDF or CSV. CRITICAL-priority tasks and deadline tasks only, with a clear time/location/owner on each line. This is the producer's quick reference — what cannot slip." },
      { type: "p", text: "Print it for: yourself, the production manager, the stage manager. Tape it to a clipboard." },

      { type: "h2", text: "Equipment Pull List" },
      { type: "p", text: "PDF or CSV. Equipment allocations sorted by pickup time, grouped by day, with vendor and quantity. Hand this to whoever is loading the truck or staging the cases." },
      { type: "p", text: "Print it for: stage techs, runners, the venue's loading bay." },

      { type: "note", text: "Every report respects your project's venue timezone for the times printed on the page. The CSV has a separate UTC column for systems that want absolute time. No more 'what timezone is this?' Slack threads." },

      { type: "h2", text: "Sharing without printing" },
      { type: "p", text: "Click SHARE in the top bar to generate a read-only link to your project for stakeholders who don't need editing access. They see the live grid, can filter, can export — but can't change anything. Good for vendors, venue staff, label reps." },
    ],
  },

  // ─── 10. Shortcuts & tips ─────────────────────────────────────────────────
  {
    id: "shortcuts-tips",
    title: "Keyboard Shortcuts & Pro Tips",
    summary: "Every keystroke that matters + the moves your future self will thank you for.",
    blocks: [
      { type: "h2", text: "Shortcuts you'll use every day" },
      { type: "kbd", combo: "Cmd K", label: "Open the AI command palette" },
      { type: "kbd", combo: "Shift /", label: "Open this help drawer from anywhere" },
      { type: "kbd", combo: "Esc", label: "Close any open dialog, drawer, or palette" },
      { type: "kbd", combo: "Enter", label: "Commit an inline-edit field" },

      { type: "h2", text: "Shortcuts in the grid" },
      { type: "kbd", combo: "Click", label: "Select a row" },
      { type: "kbd", combo: "Shift Click", label: "Range-select between last selection and target" },
      { type: "kbd", combo: "Cmd Click", label: "Toggle individual rows in the selection" },
      { type: "kbd", combo: "Double Click", label: "On a task title — open the full task detail drawer" },
      { type: "kbd", combo: "Right Click", label: "Open the cell history drawer (audit log) for any row" },

      { type: "h2", text: "Pro tips" },
      { type: "ul", items: [
        "The NOW indicator (red horizontal line in the grid) updates every 30 seconds. Click NOW in the top bar to scroll to it from anywhere.",
        "Right-click any row to see its full change history — every edit, with timestamp and actor. Great when someone says 'I didn't move that, you did'.",
        "Filters and density mode persist in your browser. Different team members can have different default views without stepping on each other.",
        "The audit log (top bar, scroll icon) shows every change across the whole project. Filter by user, date range, or resource type.",
        "When you're scheduling a fresh day, lay down quick-add titles first, then move things around. Editing six rows is faster than planning six rows perfectly in your head.",
        "Lock only your true anchors — doors open, downbeat, curfew. Locking everything just turns the schedule into a Gantt chart again.",
      ]},

      { type: "h2", text: "Stuck?" },
      { type: "p", text: "If a feature isn't behaving the way these docs say it should, check the audit log first — odds are someone (often a past you) made a change that surprised current you. The audit log doesn't lie." },
      { type: "p", text: "If the app itself misbehaves (blank screens, save errors), reload the page. Your work is saved server-side as you type, so you won't lose anything. If the problem persists, screenshot it and send it to your account contact." },
      { type: "note", text: "These docs cover the main flows. The corners — recurrence edge cases, multi-user lock contention, audit log filters — are best learned by clicking around. The app won't break anything if you experiment on the demo project." },
    ],
  },
];
